<template>
  <div class="hidden md:flex items-center" v-click-outside="closeMenu">
    <button
      class="hidden md:flex items-center uppercase font-semibold text-gray-900 hover:bg-gray-50 focus:bg-gray-50 tracking-wider cursor-pointer p-2 py-4 rounded-6 mr-1"
      :class="{ 'bg-gray-50': isOpen }"
      @click="toggleMenu"
      @keydown.esc="closeMenu"
      v-click-outside="closeMenu"
      :aria-expanded="isOpen"
      :aria-controls="uid"
      aria-label="open language selector"
    >
      <span v-if="$lang === 'uk'" class="flex items-center mr-2">
        <span class="mr-1">🇺🇦</span>
        <span class="leading-none">ua</span>
      </span>
      <span v-else class="leading-none mr-2">{{ $lang }}</span>
      <svg role="presentation" focusable="false" tabindex="-1" :class="[`icon icon-xs transition`, { 'rotate-180': isOpen }]"
         
        alt=""
        svg-inline
       xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 32 19">
  <path fill="currentColor" fill-rule="evenodd"
    d="M.8 1.18a2.26 2.26 0 0 1 3.2 0L16 13.2 28.03 1.18a2.26 2.26 0 0 1 3.2 3.2L17.6 17.97c-.88.9-2.31.9-3.2 0L.8 4.39a2.26 2.26 0 0 1 0-3.2Z"
    clip-rule="evenodd" />
</svg>
    </button>
    <Transition
      enter-active-class="transition duration-300"
      leave-active-class="transition duration-300"
      enter-from-class="translate-x-1/2 opacity-0"
      leave-to-class="translate-x-1/2 opacity-0"
    >
      <nav v-show="isOpen" class="absolute right-0 top-20 mt-2 xl:mt-4" :id="uid">
        <ul class="list-none bg-gray-50 shadow-2 w-full min-h-screen">
          <li v-for="language in languages" :key="`lang-${language.isoCode}`">
            <a
              class="flex justify-center w-full border-b-2 border-gray-100 hover:bg-white text-gray-600 text-16 font-bold tracking-wider py-4 px-12"
              :class="{ 'text-brand-800 underline': language.isoCode === $lang }"
              :href="language.url"
              @click.prevent="onLanguageClick(language.isoCode, language.url)"
            >
              {{ language.label }}
            </a>
          </li>
        </ul>
      </nav>
    </Transition>
  </div>
</template>

<script lang="ts" setup>
import Cookie, { COOKIE_LANG } from '@gifsa/cookie-utils'
import {
  HeaderLanguageItemClick,
  HeaderLanguageMenuOpen,
} from '@core/analytics/core-events/navigation'
import { ClickOutside as vClickOutside } from '@core/directives/click-outside'
import type { AlternateLanguage } from './types'

const uid = useId()

const props = defineProps<{ alternateLanguages: AlternateLanguage[] }>()

const { $lang, $track } = useNuxtApp()
const { name } = useRuntimeConfig().public

const isOpen = ref(false)

const languages = computed(() => {
  return getLanguageSelections(name).map((language) => {
    const url = props.alternateLanguages.find((l) => l.language === language.isoCode)?.slug
    return {
      isoCode: language.isoCode,
      label: language.label,
      url: url ?? getHomeUrl(language.isoCode),
    }
  })
})

async function onLanguageClick(lang: string, url: string) {
  await $track(HeaderLanguageItemClick(lang))
  document.cookie = Cookie.generate(COOKIE_LANG, lang)
  window.location.href = url
}

async function toggleMenu() {
  isOpen.value = !isOpen.value
  if (isOpen.value) {
    await $track(HeaderLanguageMenuOpen())
  }
}

function closeMenu() {
  isOpen.value = false
}
</script>
